// A SZERKESZTŐ-MÉDIA ELŐKÉSZÍTÉSE feltöltés előtt — egy szabály a belső és a partner-portál feltöltőjének:
// típus-fehérlista → (nagy fotónál) kicsinyítés → méret-plafon. A típus-lista és a plafon a SZERZŐDÉSBŐL jön
// (`EDITOR_MEDIA_TYPES` / `editorMediaMaxBytes`: kép 5 MB, videó 25 MB) — a szerver ugyanezt kényszeríti.
//
// MIÉRT KICSINYÍTÜNK (user-döntés: „best practice, ha nem nagy munka"): a telefonos fotó gyakran 6–12 MB, és
// eddig egyszerűen elutasítottuk. A túl nagy JPEG/PNG/WEBP-t a böngészőben legfeljebb `MAX_EDGE_PX` oldalra
// méretezzük és JPEG-be mentjük; ha így is plafon fölött marad, a jól ismert „legfeljebb 5 MB" hiba jön. A GIF
// kimarad (az animáció elveszne), a HEIC pedig külön könyvtárat kívánna — arra marad a típus-hibaüzenet.
import { editorMediaMaxBytes, EDITOR_MEDIA_TYPES } from '@atlas/contracts';

/** A kicsinyített kép hosszabbik oldala (px) — képernyőképnek és fotónak is bőven elég, a fájl pedig kicsi. */
const MAX_EDGE_PX = 2560;
/** A JPEG-minőség a kicsinyítéskor — a szemre veszteségmentes és a fájlméret közti szokásos egyensúly. */
const JPEG_QUALITY = 0.85;
/** A kicsinyíthető (raszteres, nem animált) típusok. */
const DOWNSCALABLE = new Set(['image/jpeg', 'image/png', 'image/webp']);

export type EditorMediaType = (typeof EDITOR_MEDIA_TYPES)[number];

export type PreparedEditorMedia =
  { ok: true; file: File; contentType: EditorMediaType } | { ok: false; error: 'type' | 'size' };

/** Kicsinyítés JPEG-be; `null`, ha a böngésző nem tudja (nincs `createImageBitmap` / canvas) vagy hiba van. */
async function downscaleToJpeg(file: File): Promise<File | null> {
  if (typeof createImageBitmap !== 'function') return null;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    );
    if (!blob) return null;
    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg' });
  } catch {
    // a dekódolás/rajzolás hibája nem végzetes: a hívó a méret-hibát adja (nem néma)
    return null;
  }
}

/** Típus → (kicsinyítés) → méret. Soha nem dob; a hibát a hívó fordítja (`richText.imageError.*`). */
export async function prepareEditorMedia(file: File): Promise<PreparedEditorMedia> {
  const contentType = EDITOR_MEDIA_TYPES.find((t) => t === file.type);
  if (contentType === undefined) return { ok: false, error: 'type' };
  if (file.size <= editorMediaMaxBytes(contentType)) return { ok: true, file, contentType };
  if (!DOWNSCALABLE.has(contentType)) return { ok: false, error: 'size' };
  const smaller = await downscaleToJpeg(file);
  if (smaller === null || smaller.size > editorMediaMaxBytes('image/jpeg')) {
    return { ok: false, error: 'size' };
  }
  return { ok: true, file: smaller, contentType: 'image/jpeg' };
}
